import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  formatSettingsDate,
  formatSubscriptionCycle,
  formatSubscriptionTier,
} from 'utils/settingsDisplay';
import { ACCOUNT_SETTINGS_TAB_KEY } from 'utils/accountSettings';

function DetailRow({ label, value }) {
  const text = value == null || value === '' ? '—' : String(value);
  return (
    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, pt: 0.15 }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, textAlign: 'right', minWidth: 0, wordBreak: 'break-word' }}
      >
        {text}
      </Typography>
    </Stack>
  );
}

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default function ProfileMenuDetails({ user, onNavigate, showManageAccount = true }) {
  const navigate = useNavigate();
  const planLabel = formatSubscriptionTier(user?.subscription_tier);
  const cycleLabel = formatSubscriptionCycle(user?.subscription_cycle);
  const isAdmin = String(user?.role || '').trim().toLowerCase() === 'admin';

  const openAccountSettings = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(ACCOUNT_SETTINGS_TAB_KEY, 'my-account');
    }
    onNavigate?.();
    navigate('/settings?tab=my-account');
  };

  return (
    <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
      {isAdmin ? (
        <>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label="Admin"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            <Chip size="small" label={planLabel} variant="outlined" sx={{ fontWeight: 600 }} />
          </Stack>
          <Divider />
        </>
      ) : null}

      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        Account
      </Typography>
      <Stack spacing={0.75}>
        <DetailRow label="Username" value={user?.username} />
        <DetailRow label="Email" value={user?.email} />
        {user?.phone ? <DetailRow label="Phone" value={user.phone} /> : null}
        {user?.country ? <DetailRow label="Country" value={user.country} /> : null}
      </Stack>

      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1.2, pt: 0.25 }}>
        Subscription
      </Typography>
      <Stack spacing={0.75}>
        <DetailRow label="Plan" value={planLabel} />
        <DetailRow label="Billing" value={cycleLabel} />
        <DetailRow label="Started" value={formatSettingsDate(user?.subscription_started_at)} />
        <DetailRow label="Expires" value={formatSettingsDate(user?.subscription_expires_at)} />
      </Stack>

      {showManageAccount ? (
        <Box sx={{ pt: 0.5 }}>
          <Button fullWidth size="small" variant="outlined" onClick={openAccountSettings}>
            Account & subscription
          </Button>
        </Box>
      ) : null}
    </Stack>
  );
}

ProfileMenuDetails.propTypes = {
  user: PropTypes.object,
  onNavigate: PropTypes.func,
  showManageAccount: PropTypes.bool,
};
